import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Card, Btn, Empty, Modal } from "../components/UI";
import { FLabel, FInput, FSelect } from "./engagement/FormControls";
import { MessageSquarePlus, Send, ShieldCheck, ArrowLeft, Mail, BookmarkCheck, Check, CheckCheck } from "lucide-react";

const MESSAGE_TYPES = [
  { value: "custom", label: "Custom Message" },
  { value: "reminder", label: "Reminder" },
  { value: "appointment", label: "Appointment Related" },
  { value: "broadcast", label: "Broadcast / Announcement" },
];

// Small variable-substitution helper. Never inject anything a user did not ask for.
function fillTemplate(text, client) {
  if (!text) return "";
  return text
    .replace(/{{first_name}}/g, client?.first_name || "there")
    .replace(/{{last_name}}/g, client?.last_name || "")
    .replace(/{{appointment_date}}/g, "your upcoming visit");
}

// WhatsApp-style receipt indicator (only for messages the current staff user sent).
function ReceiptTicks({ msg }) {
  if (!msg) return null;
  const read = !!msg.read_at;
  const delivered = !!msg.delivered_at;
  const title = read
    ? `Read ${dayjs(msg.read_at).format("MMM D, h:mm A")}${msg.read_audit?.actor_name ? ` by ${msg.read_audit.actor_name}` : ""}`
    : delivered
      ? `Delivered ${dayjs(msg.delivered_at).format("MMM D, h:mm A")}`
      : "Sent";
  return (
    <span data-testid={`receipt-${msg.id}`} data-state={read ? "read" : delivered ? "delivered" : "sent"} title={title}
      className="inline-flex items-center text-white/80">
      {read || delivered ? (
        <CheckCheck size={13} className={read ? "text-[#4FC3F7]" : "text-white/70"} />
      ) : (
        <Check size={13} className="text-white/70" />
      )}
    </span>
  );
}

export function MessagesPanel({ client, editable }) {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [composeForm, setComposeForm] = useState({ subject: "", message_type: "custom", body: "" });
  const [composeBusy, setComposeBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [pickedTemplate, setPickedTemplate] = useState("");
  const scrollBoxRef = useRef(null);

  const loadThreads = () => api.get(`/messages/threads?client_id=${client.id}`)
    .then((r) => setThreads(r.data)).catch((e) => toast.error(apiError(e)));

  const scrollToLatest = () => {
    // Only scroll the messages container, never the outer page. Avoids the
    // whole client-detail page jumping to the bottom when a thread opens.
    const box = scrollBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  };

  const openThread = (tid) => {
    setActiveId(tid);
    api.get(`/messages/threads/${tid}`).then((r) => {
      setActiveThread(r.data.thread);
      setMessages(r.data.messages);
      // update the list-view unread badge to match the just-read state
      setThreads((ts) => ts.map((t) => (t.id === tid ? { ...t, unread_for_staff: false } : t)));
      setTimeout(scrollToLatest, 40);
    }).catch((e) => toast.error(apiError(e)));
  };

  useEffect(() => { if (client?.id) loadThreads(); }, [client?.id]); // eslint-disable-line
  useEffect(() => {
    if (!composeOpen) return;
    api.get("/messages/templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  }, [composeOpen]);

  const applyTemplate = (key) => {
    setPickedTemplate(key);
    if (!key) return;
    const t = templates.find((x) => (x.key || x.id) === key);
    if (!t) return;
    setComposeForm({
      subject: fillTemplate(t.subject, client),
      message_type: t.message_type || "custom",
      body: fillTemplate(t.body, client),
    });
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/messages/threads/${activeId}/messages`, { body: reply.trim() });
      setReply("");
      openThread(activeId); loadThreads();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  const submitCompose = async (e) => {
    e.preventDefault();
    if (!composeForm.subject.trim() || !composeForm.body.trim()) {
      return toast.error("Subject and body are both required");
    }
    setComposeBusy(true);
    try {
      const r = await api.post("/messages/threads", { ...composeForm, client_id: client.id });
      toast.success("Secure message sent");
      setComposeOpen(false);
      setComposeForm({ subject: "", message_type: "custom", body: "" });
      setPickedTemplate("");
      loadThreads();
      openThread(r.data.thread.id);
    } catch (err) { toast.error(apiError(err)); }
    finally { setComposeBusy(false); }
  };

  if (activeId && activeThread) {
    return (
      <div data-testid="messages-thread-view" className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <button data-testid="messages-back-to-list" onClick={() => { setActiveId(null); setActiveThread(null); setMessages([]); }}
            className="flex items-center gap-1.5 text-xs font-bold text-[#7B6157] hover:text-[#4E342E]">
            <ArrowLeft size={13} /> Back to all messages
          </button>
          <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B] flex items-center gap-1">
            <ShieldCheck size={12} /> HIPAA Secure
          </div>
        </div>
        <Card className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-heading text-sm font-bold text-[#3E2723]">{activeThread.subject}</div>
              <div className="text-[11px] text-[#7B6157]">{activeThread.message_type} · started {dayjs(activeThread.created_at).format("MM/DD/YYYY h:mm A")} by {activeThread.created_by_name}</div>
            </div>
          </div>
          <div ref={scrollBoxRef} className="mt-4 space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {messages.map((m) => {
              const mine = m.from_side === "staff";
              return (
                <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#3E2723]"}`}>
                    <div className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${mine ? "text-white/70" : "text-[#7B6157]"}`}>
                      {m.from_name} · {dayjs(m.created_at).format("MMM D h:mm A")}
                    </div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                    {mine && (
                      <div className="mt-1 flex justify-end items-center gap-1 text-[10px] text-white/70">
                        <ReceiptTicks msg={m} />
                        <span>
                          {m.read_at ? `Read ${dayjs(m.read_at).format("h:mm A")}` : m.delivered_at ? "Delivered" : "Sent"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {editable && (
            <form onSubmit={submitReply} className="mt-4 border-t border-[#F1EEE7] pt-3 flex items-end gap-2">
              <textarea data-testid="messages-reply-input" rows={2} value={reply} onChange={(e) => setReply(e.target.value)}
                placeholder="Type a HIPAA-safe reply. Never share credentials or verification codes."
                className="flex-1 rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
              <Btn data-testid="messages-send-btn" type="submit" disabled={sending}>
                <Send size={14} /> {sending ? "Sending…" : "Send"}
              </Btn>
            </form>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="messages-panel" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-heading font-bold text-[#3E2723]">Secure Messages</div>
          <div className="text-[11px] text-[#7B6157]">Encrypted in transit, audited on send and read. Clients see them in their portal and receive a no-PHI email notification.</div>
        </div>
        {editable && (
          <Btn data-testid="messages-compose-btn" onClick={() => setComposeOpen(true)}>
            <MessageSquarePlus size={14} /> New Message
          </Btn>
        )}
      </div>

      {threads.length === 0 ? (
        <Empty text="No messages yet. Start a secure conversation with this client." />
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <button key={t.id} type="button" data-testid={`message-thread-${t.id}`} onClick={() => openThread(t.id)}
              className="w-full text-left rounded-xl border border-[#EFEBE0] bg-white p-3 hover:bg-[#FDFCF8] transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-[#7B6157] shrink-0" />
                    <span className="font-semibold text-sm text-[#3E2723] truncate">{t.subject}</span>
                    {t.unread_for_staff && <span data-testid={`thread-unread-${t.id}`} className="rounded-full bg-[#C62828] px-2 py-0.5 text-[9px] font-bold uppercase text-white">Unread</span>}
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#7B6157]">
                    {t.message_type} · last message {dayjs(t.last_message_at).format("MM/DD/YYYY h:mm A")} from {t.last_message_from === "staff" ? "care team" : "client"}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Modal open={composeOpen} onClose={() => setComposeOpen(false)} title="New Secure Message" wide>
        <form onSubmit={submitCompose} className="space-y-3" data-testid="messages-compose-form">
          <div className="text-[11px] text-[#7B6157] flex items-center gap-1">
            <ShieldCheck size={12} /> HIPAA note: email notifications never include the message body. The full message is only visible to the client after they log into the portal.
          </div>
          {templates.length > 0 && (
            <div>
              <FLabel><span className="inline-flex items-center gap-1"><BookmarkCheck size={12} /> Start from a template (optional)</span></FLabel>
              <FSelect data-testid="messages-template-picker" value={pickedTemplate} placeholder="No template - start blank" onChange={(e) => applyTemplate(e.target.value)}>
                {templates.map((t) => <option key={t.key || t.id} value={t.key || t.id}>{t.label}{t.is_builtin ? "" : " (custom)"}</option>)}
              </FSelect>
              <p className="mt-1 text-[11px] text-[#7B6157]">Placeholders like {"{{first_name}}"} are filled in automatically for {client?.first_name}.</p>
            </div>
          )}
          <div><FLabel>Subject</FLabel><FInput data-testid="messages-subject-input" value={composeForm.subject}
            onChange={(e) => setComposeForm((s) => ({ ...s, subject: e.target.value }))} placeholder="E.g., Upcoming appointment reminder" required /></div>
          <div><FLabel>Message Type</FLabel>
            <FSelect data-testid="messages-type-input" value={composeForm.message_type}
              onChange={(e) => setComposeForm((s) => ({ ...s, message_type: e.target.value }))}>
              {MESSAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </FSelect>
          </div>
          <div><FLabel>Message</FLabel>
            <textarea data-testid="messages-body-input" rows={6} value={composeForm.body}
              onChange={(e) => setComposeForm((s) => ({ ...s, body: e.target.value }))}
              placeholder="Write your secure message. Do not include verification codes or password reset instructions here."
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" required />
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setComposeOpen(false)}>Cancel</Btn>
            <Btn data-testid="messages-compose-submit" type="submit" disabled={composeBusy}>
              <Send size={13} /> {composeBusy ? "Sending…" : "Send Secure Message"}
            </Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
